import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { CallbackPage } from "./pages/CallbackPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { PendingPage } from "./pages/PendingPage";
import { LaunchesPage } from "./pages/LaunchesPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { LaunchTypesPage } from "./pages/launchTypes/LaunchTypesPage";
import { LaunchTypeBoardPage } from "./pages/launchTypes/LaunchTypeBoardPage";
import { BoardPage } from "./pages/BoardPage";
import { TasksPage } from "./pages/tasks/TasksPage";
import { LandingsPage } from "./pages/landings/LandingsPage";
import { LandingEditorPage } from "./pages/landings/LandingEditorPage";
import { AdminPage } from "./pages/admin/AdminPage";
import { AdminRolesPage } from "./pages/admin/AdminRolesPage";
import { TasksAuditPage } from "./pages/admin/TasksAuditPage";
import { AppLayout } from "./components/AppLayout";
import { useSession } from "./hooks/useSession";
import { usePermission } from "./hooks/usePermission";
import { useSettings } from "./hooks/useSettings";
import { tokenStore } from "./api/token";
import { ApiError } from "./api/client";
import type { Permissions } from "./types/domain";

/**
 * Подбирает первый доступный раздел в зависимости от прав пользователя.
 * Используется для дефолтного редиректа `/` и неизвестных маршрутов: если
 * у юзера урезанная роль, корень переходит на первое доступное место.
 * Возвращает null, если у юзера нет ни одного раздела — тогда показываем
 * NoAccessPage с подсказкой обратиться к администратору.
 */
function firstAccessibleRoute(perms: Permissions | undefined): string | null {
  if (!perms) return null;
  if (perms.launches?.view) return "/launches";
  if (perms.tasks?.view || perms.tasks?.viewOwn) return "/tasks";
  if (perms.landings?.view) return "/landings";
  if (perms.templates?.view) return "/templates";
  if (perms.launchTypes?.view) return "/launch-types";
  if (perms.admin?.manageUsers) return "/admin";
  if (perms.admin?.manageRoles) return "/admin/roles";
  if (perms.admin?.viewAudit) return "/admin/tasks-audit";
  return null;
}

function NoAccessPage({ email }: { email?: string }) {
  return (
    <div className="auth-splash">
      <div className="auth-splash-card">
        <div className="auth-splash-title">Нет доступа</div>
        <div className="auth-splash-sub">
          {email
            ? `У аккаунта ${email} нет прав ни на один раздел. `
            : "У этого аккаунта нет прав ни на один раздел. "}
          Обратитесь к администратору, чтобы он назначил вам подходящую роль.
        </div>
        <button
          className="auth-splash-action"
          onClick={() => {
            tokenStore.clear();
            window.location.href = "/login";
          }}
        >
          Войти другим аккаунтом
        </button>
      </div>
    </div>
  );
}

function DefaultRoute() {
  const session = useSession();
  const perms = session.data?.permissions;
  const dest = firstAccessibleRoute(perms);
  if (!dest) return <NoAccessPage email={session.data?.email} />;
  return <Navigate to={dest} replace />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hasToken = Boolean(tokenStore.get());
  const session = useSession();

  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (session.isLoading) {
    return (
      <div className="auth-splash">
        <div className="auth-splash-card">
          <div className="auth-splash-title">Загружаем сессию…</div>
        </div>
      </div>
    );
  }
  if (session.isError) {
    const err = session.error;
    // Legacy code path for servers still on the allowlist table.
    if (err instanceof ApiError && err.status === 403 && err.code === "not_in_allowlist") {
      return <PendingPage />;
    }
    if (err instanceof ApiError && err.status === 403 && err.code === "pending_approval") {
      return <PendingPage />;
    }
    return <Navigate to="/login" replace />;
  }
  // Pending accounts are authenticated but not yet granted access.
  if (session.data && session.data.role === "pending") {
    return <PendingPage email={session.data.email} />;
  }
  return <>{children}</>;
}

/** Гард для /tasks: пускает при tasks.view ИЛИ tasks.viewOwn. Сделано
 *  отдельно от RequirePermission, чтобы у дефолтной шкалы view/create/edit/
 *  delete не появлялся OR-вариант на каждом ресурсе. */
function TasksRouteGate({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const perms = session.data?.permissions;
  const ok = perms?.tasks?.view === true || perms?.tasks?.viewOwn === true;
  if (session.data && !ok) {
    return <ForbiddenPage email={session.data.email} message="Нет прав для этой страницы." />;
  }
  return <>{children}</>;
}

function RequirePermission({
  resource,
  action,
  children,
}: {
  resource: Parameters<typeof usePermission>[0];
  action: Parameters<typeof usePermission>[1];
  children: React.ReactNode;
}) {
  const session = useSession();
  const ok = usePermission(resource, action);
  if (session.data && !ok) {
    return <ForbiddenPage email={session.data.email} message="Нет прав для этой страницы." />;
  }
  return <>{children}</>;
}

function AppShell() {
  useSettings(); // applies theme/density to <body>
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<DefaultRoute />} />
        <Route
          path="/launches"
          element={
            <RequirePermission resource="launches" action="view">
              <LaunchesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/board"
          element={
            <RequirePermission resource="launches" action="view">
              <BoardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tasks"
          element={
            <TasksRouteGate>
              <TasksPage />
            </TasksRouteGate>
          }
        />
        <Route
          path="/landings"
          element={
            <RequirePermission resource="landings" action="view">
              <LandingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/landings/:id"
          element={
            <RequirePermission resource="landings" action="view">
              <LandingEditorPage />
            </RequirePermission>
          }
        />
        <Route
          path="/templates"
          element={
            <RequirePermission resource="templates" action="view">
              <TemplatesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/launch-types"
          element={
            <RequirePermission resource="launchTypes" action="view">
              <LaunchTypesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/launch-types/:id/edit"
          element={
            <RequirePermission resource="launchTypes" action="view">
              <LaunchTypeBoardPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin"
          element={
            <RequirePermission resource="admin" action="manageUsers">
              <AdminPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <RequirePermission resource="admin" action="manageRoles">
              <AdminRolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/tasks-audit"
          element={
            <RequirePermission resource="admin" action="viewAudit">
              <TasksAuditPage />
            </RequirePermission>
          }
        />
        <Route path="*" element={<DefaultRoute />} />
      </Routes>
    </AppLayout>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      />
    </Routes>
  );
}
